import { useEffect, useRef, useState } from "react";
import { observer } from 'mobx-react-lite';
import Button from "../../../components/Button";
import ThreeDot from "../../../components/Threedots";
import apiKeyStore from "../../../stores/apiKeyStore";
import modalStore from "../../../stores/modalStore";
import lock_icon from "../../../assets/steps-lock.webp";
import remove_icon from '../../../assets/remove.webp';
import question_mark from "../../../assets/question_cirlce.webp";
import copy_icon from "../../../assets/copy-clickboard.webp";
import copy_done from "../../../assets/copy-done.webp";
import mixpanel from "../../../helpers/mixpanel";

const formatDate = (date) => {
  if (!date) return "Never";
  return new Date(date).toLocaleDateString("en-US", { year: "numeric", month: "short", day: "numeric" });
}

// Shown once right after creation — the raw key is never retrievable again.
const NewKeyContent = ({ rawKey }) => {
  const [copied, setCopied] = useState(false);
  const snippet = `asostudio auth login --api-key ${rawKey}`;
  return (
    <div style={{ display: "flex", flexDirection: "column", padding: "0 24px 24px 24px", maxWidth: 560 }}>
      <span className="setting_custom_desc" style={{ marginTop: 0 }}>
        Copy your API key now — for security reasons it will <b>never be shown again</b>.
      </span>
      <div
        className="cursor"
        onClick={() => {
          navigator.clipboard.writeText(rawKey);
          setCopied(true);
          setTimeout(() => setCopied(false), 2000);
        }}
        style={{
          display: "flex", alignItems: "center", justifyContent: "space-between",
          backgroundColor: "rgba(255,255,255,0.06)", border: "1px solid rgba(255,255,255,0.15)",
          borderRadius: 8, padding: "12px 16px", marginTop: 16
        }}>
        <span style={{ color: "white", fontFamily: "monospace", fontSize: 13, wordBreak: "break-all", marginRight: 12 }}>
          {rawKey}
        </span>
        <img src={copied ? copy_done : copy_icon} style={{ width: 20, height: 20 }} alt="copy" />
      </div>
      <span className="setting_page_subtitle" style={{ marginTop: 24 }}>
        Use it with the CLI / MCP
      </span>
      <div style={{
        backgroundColor: "rgba(0,0,0,0.4)", borderRadius: 8, padding: "12px 16px", marginTop: 8
      }}>
        <span style={{ color: "#9fe870", fontFamily: "monospace", fontSize: 12, wordBreak: "break-all" }}>
          {snippet}
        </span>
      </div>
      <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 24 }}>
        <Button text="Done" onClick={() => modalStore.setShow(false)} style={{ width: 120 }} />
      </div>
    </div>
  )
}

const CreateKeyContent = observer(() => {
  const [name, setName] = useState("");
  const [loading, setLoading] = useState(false);

  const create = async () => {
    if (loading) return;
    setLoading(true);
    try {
      const created = await apiKeyStore.createKey(name || "Untitled Key");
      mixpanel.track("ASO_SETTINGS_APIKEY_CREATED");
      if (created && created.key) {
        modalStore.setContent("API Key Created", <NewKeyContent rawKey={created.key} />, false);
      } else {
        modalStore.setShow(false);
      }
    } catch (e) {
      modalStore.setShow(false);
    }
    setLoading(false);
  }

  return (
    <div style={{ display: "flex", flexDirection: "column", padding: "0 24px 24px 24px", minWidth: 420 }}>
      <span className="setting_custom_desc" style={{ marginTop: 0 }}>
        Give your key a name so you can recognize it later (e.g. "MacBook CLI", "Claude MCP").
      </span>
      <input
        autoFocus
        value={name}
        onChange={(e) => setName(e.target.value)}
        onKeyDown={(e) => { if (e.key === "Enter") create(); }}
        placeholder="Key name"
        style={{
          backgroundColor: "rgba(255,255,255,0.06)", border: "1px solid rgba(255,255,255,0.15)",
          borderRadius: 8, padding: "12px 16px", marginTop: 16, color: "white", fontSize: 14, outline: "none"
        }}
      />
      <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 24 }}>
        <Button text="Create Key" loading={loading} onClick={create} style={{ width: 160 }} />
      </div>
    </div>
  )
});

const ApiKeyItem = ({ _id, name, prefix, createdAt, lastUsedAt }) => {
  const threedot = useRef();
  return (
    <div className="settings_account_item">
      <div className="store_icon_container">
        <img src={lock_icon} className="store_icon" alt="API key" />
      </div>
      <div className="settings_account_container">
        <span className="settings_account_title">
          {name}
        </span>
        <span className="settings_account_desc" style={{ fontFamily: "monospace" }}>
          {prefix}…
        </span>
        <span className="settings_account_desc">
          Created {formatDate(createdAt)} · Last used {formatDate(lastUsedAt)}
        </span>
      </div>
      <ThreeDot ref={threedot}>
        <div onClick={() => {
          threedot.current.closeModal();
          modalStore.setContentSure(
            "Revoke",
            question_mark,
            "Any CLI or MCP client using this key will stop working immediately. Are you sure?",
            "Yes, Revoke Key",
            "Cancel",
            async () => {
              await apiKeyStore.revokeKey(_id);
              mixpanel.track("ASO_SETTINGS_APIKEY_REVOKED");
            }
          )
        }} className='add_app_remove_icon_container cursor'>
          <img src={remove_icon} className='add_app_remove_icon' alt="revoke" />
          <span className='add_app_remove_text'>
            Revoke Key
          </span>
        </div>
      </ThreeDot>
    </div>
  )
}

const ApiKeys = observer(() => {

  const { keys } = apiKeyStore;

  useEffect(() => {
    mixpanel.track("ASO_SETTINGS_APIKEYS_TAB_VIEW");
    apiKeyStore.getKeys();
  }, [])

  return (
    <div className="setting_page_container">
      <div className="settings_page_title_container" style={{ flexDirection: "column" }}>
        <span className="setting_page_title">
          API Keys
        </span>
        <span className="setting_custom_desc">
          Use API keys with the aso.studio CLI and MCP server to manage metadata, reviews, pricing and analytics from your terminal or AI agent.
          Your App Store Connect key always stays on your machine — this key only authorizes your aso.studio account.
        </span>
      </div>
      <div style={{ display: "flex", marginLeft: 30, marginTop: 60, flexDirection: "column" }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", maxWidth: 720 }}>
          <span className="setting_page_subtitle">
            Active Keys
          </span>
          <Button text="Create API Key" style={{ width: 180 }} onClick={() => {
            modalStore.setContent("Create API Key", <CreateKeyContent />, false);
          }} />
        </div>
        <div style={{ display: "flex", flexWrap: "wrap", marginTop: 12 }}>
          {
            keys.length === 0 ?
              <span className="setting_custom_desc" style={{ marginTop: 16 }}>
                No API keys yet. Create one to use the CLI or connect an AI agent via MCP.
              </span>
              :
              keys.map((item, index) => {
                return <ApiKeyItem key={"apiKeyItem#" + index} {...item} />
              })
          }
        </div>
      </div>
    </div>
  )
});

export default ApiKeys;
